import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';

import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useGetActiveOfferTemplates } from '@/views/offers/templates/util/useGetActiveOfferTemplates.util.ts';

import { OfferPrintTemplateTarget } from './offerPrintTemplate.util.ts';
import { useUpdateOfferPrintTemplate } from './useUpdateOfferPrintTemplate.util.ts';

type Props = {
  multiProcessOfferGroupId: string;
  onAssigned: () => void;
  processId: string;
  target: OfferPrintTemplateTarget;
  variant: 'assigned' | 'missing';
};

export const ProcessOfferPrintTemplatePicker: React.FC<Props> = ({
  multiProcessOfferGroupId,
  onAssigned,
  processId,
  target,
  variant,
}) => {
  const { t } = useTranslationWithPrefix('views.process.offer.pdfPreview.template');
  const { activeOfferTemplates, isLoading: isLoadingTemplates } = useGetActiveOfferTemplates();
  const { isUpdating, updateOfferPrintTemplate } = useUpdateOfferPrintTemplate();
  const [selected, setSelected] = useState<'' | number>(target.offerPrintId ?? '');

  const options = React.useMemo(() => {
    const active = activeOfferTemplates.map(it => ({ id: it.offerPrintId, name: it.name }));
    return target.offerPrintId != null && !active.some(it => it.id === target.offerPrintId)
      ? [
          { id: target.offerPrintId, name: target.offerPrintName ?? `#${target.offerPrintId}` },
          ...active,
        ]
      : active;
  }, [activeOfferTemplates, target.offerPrintId, target.offerPrintName]);

  const handleSave = async () => {
    if (selected === '') {
      return;
    }
    await updateOfferPrintTemplate({
      multiProcessOfferGroupId,
      offerPrintId: selected,
      processId,
      processOfferId: target.level === 'offer' ? target.processOfferId : undefined,
    });
    onAssigned();
  };

  const isDirty = selected !== '' && selected !== target.offerPrintId;
  const isBusy = isUpdating || isLoadingTemplates;

  const select = (
    <TextField
      disabled={isBusy}
      fullWidth
      label={t('label')}
      onChange={event => setSelected(Number(event.target.value))}
      select
      size="small"
      value={options.some(it => it.id === selected) ? selected : ''}
    >
      {options.map(option => (
        <MenuItem key={option.id} value={option.id}>
          {option.name}
        </MenuItem>
      ))}
    </TextField>
  );

  const saveButton = (
    <Button disabled={!isDirty || isBusy} onClick={handleSave} size="small" variant="contained">
      {variant === 'missing' ? t('saveAndGenerate') : t('save')}
    </Button>
  );

  if (variant === 'missing') {
    return (
      <Stack sx={{ alignItems: 'center', height: '100%', justifyContent: 'center', p: 4 }}>
        <Alert severity="warning" sx={{ maxWidth: 520, width: '100%' }}>
          <AlertTitle>{t('missingTitle')}</AlertTitle>
          <Typography component="div" variant="f14Regular">
            {t('missingDescription')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 2 }}>
            {select}
            {saveButton}
          </Stack>
          {options.length === 0 && !isLoadingTemplates && (
            <Typography
              component="div"
              sx={{ color: themeColors.greyDark, mt: 1 }}
              variant="f12Regular"
            >
              {t('noTemplates')}
            </Typography>
          )}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        borderBottom: `1px solid ${themeColors.stroke}`,
        px: 2,
        py: 1,
      }}
    >
      {select}
      {saveButton}
      <Typography
        component="div"
        sx={{ color: themeColors.greyDark, flexShrink: 0 }}
        variant="f12Regular"
      >
        {target.level === 'offer' ? t('levelOffer') : t('levelGroup')}
      </Typography>
    </Stack>
  );
};
